import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { filterPermittedNavList } from './FeatureHider';
import styles from "./Root.module.css";
import { NavLink, useNavigate } from 'react-router-dom';
import { VERTICAL_BAR_WIDTH } from './Root';
import MoreSvg from "./icons/more-horiz.svg";
import SideDisplay from 'common-ui-components/components/SideDisplay/SideDisplay';
import { redirectToPage } from 'app/common/redirects/Redirects';
import { getNavigationList } from './NavList';
import { getCurrentlyRederedModule, UserDetailsContext } from 'App';

const routeChunkImporters = {
    HOME: () => import("app/home/Home"),
    INVENTORY: () => import("app/inventory-new/base/Inventory"),
    MANAGE: () => import("app/manage/base/Devices"),
    PATCH: () => import("app/patch/Patch"),
    REMOTE: () => import("app/remote/base/Tools"),
    ALERTS: () => import("app/inventory-new/alerts/Alerts"),
    REPORTS: () => import("app/reports/base/ReportsBase"),
    ENROLLMENT: () => import("app/enrollment-new/base/Enrollment"),
    SETTINGS: () => import("app/settings/base/SettingsPage"),
};

const prefetchedModules = new Set();

const RootSidebar = ({verified=null}) => {
    const listRef = useRef(null);
    const itemRefs = useRef([]);
    const [hiddenItems, setHiddenItems] = useState([]);
    const [isMeasuring, setIsMeasuring] = useState(true);
    const history=useNavigate();

    const {currentCustomerId} = useContext(UserDetailsContext);

    const currentModule = getCurrentlyRederedModule();

    const navigationList = useMemo(() => {
        return getNavigationList(currentModule, currentCustomerId ?? 1);
    }, [currentModule, currentCustomerId]);

    const permittedNavList = filterPermittedNavList(
        navigationList,
        "Root"
    );

    const [visibleItems,setVisibleItems]=useState(permittedNavList);

    useEffect(() => {
        if (isMeasuring) return;
        if (!hiddenItems.length) return;
        const activeItem = hiddenItems.find(i => i.key === currentModule);
        if (!activeItem) return;
        setVisibleItems(prevVisible => {
            const newVisible = [...prevVisible];
            const lastVisible = newVisible.pop();
            newVisible.push(activeItem);
            setHiddenItems(prevHidden => {
                const filtered = prevHidden.filter(i => i.key !== currentModule);
                const newHidden = [lastVisible, ...filtered];
                // console.log("newHidden:", newHidden.map(i => i.key));
                return newHidden;
            });
            return newVisible;
        });
    }, [currentModule, hiddenItems, isMeasuring]);

    useEffect(() => {
        const checkItems = () => {
            if (!listRef.current) return;
            setIsMeasuring(true);

            const containerRect = listRef.current.getBoundingClientRect();

            const visible = [];
            const hidden = [];

            itemRefs.current.forEach((el, index) => {
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const isFullyVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;
                if (isFullyVisible) 
                    visible.push(permittedNavList[index]);
                else 
                    hidden.push(permittedNavList[index]);
            });

            if (visible.length > 0 && hidden.length>0) {
                const lastVisible = visible.pop();
                hidden.unshift(lastVisible);
            }

            setVisibleItems(visible);
            setHiddenItems(hidden);

            setIsMeasuring(false);
        };

        let resizeTimer = null;

        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                checkItems(); 
            }, 150);
        };

        checkItems();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(resizeTimer);
        };
    }, []);

    const hiddenItemOnClick = (item) => {
        // console.log("Clicked:", item.key);
        // console.log("Before", visibleItems.map(i => i.key));
        // console.log("Hidden", hiddenItems.map(i => i.key));

        redirectToPage(history, item.path);
    };

    const prefetchModuleChunk = (item) => {
        const moduleKey = item?.key;
        if (!moduleKey || prefetchedModules.has(moduleKey)) return;

        const importer = routeChunkImporters[moduleKey];
        if (!importer) return;
        importer()
        prefetchedModules.add(moduleKey);
        importer().catch(() => {
            prefetchedModules.delete(moduleKey);
        });
    };

    const listActions = hiddenItems.reduce((acc, item) => {
        acc[item.name] = {
            action: () => hiddenItemOnClick(item),
            icon: item.icon,
            onMouseEnter: () => prefetchModuleChunk(item),
        };
        return acc;
    }, {});

    return (
        <ul
            ref={listRef}
            className={styles.rootVerticalBar}
            style={
                verified ? { paddingBottom: "35px" } : null
            }
        >
            {[...visibleItems,...hiddenItems].map((item,index) => {
                const isVisible = visibleItems.some(i => i.key === item.key);
                return (
                    <li 
                        key={item.path}
                        ref={(el) => (itemRefs.current[index] = el)}
                        style={
                            isMeasuring
                            ? {}
                            : {
                                visibility: isVisible ? "visible" : "hidden",
                                position: isVisible ? "static" : "absolute",
                                }
                        }
                        >
                        <NavLink
                            to={item.path}
                            className={({ isActive }) => isActive ? `${styles.navItem} ${styles.activeNavItem}` : styles.navItem}
                            onMouseEnter={() => prefetchModuleChunk(item)}
                            onFocus={() => prefetchModuleChunk(item)}
                            onTouchStart={() => prefetchModuleChunk(item)}
                        >
                            <img
                                src={item.icon}
                                alt={item.name}
                                className={styles.navIcon}
                            />
                            <span className={styles.navText}>{item.name}</span>
                        </NavLink>
                    </li>
                );
            })}
            {hiddenItems.length!=0 &&
                <SideDisplay
                    listActions={listActions} 
                    style={{backgroundColor:"#0B1A2F",textColor:"#B8BAC1",hoverColor:"#000000"}}
                >
                    <div className={styles.rootSideBarMore}
                        style={{height:VERTICAL_BAR_WIDTH,
                            width:VERTICAL_BAR_WIDTH}}
                        title={"More items"}
                    >
                        <img src={MoreSvg} />
                        <span className={styles.rootMoreText}>More</span>
                    </div>
                </SideDisplay>
            }
        </ul>
    )
}

export default RootSidebar
